/**
 * ErrorReportService
 * Service for sending error reports from frontend to backend
 * Handles error reporting with context information
 */

import { buildCorrelationDiagnosticReport } from "./http/correlationDiagnosticBuffer";

interface ErrorReportData {
    "message":     string;
    "name"?:       string;
    "stack"?:      string;
    "url"?:        string;
    "appVersion"?: string;
    "userAgent"?:  string;
    "time"?:       string;
    "contexts"?: {
        "persistedSlicesSnapshot"?: Record<string, unknown>;
        "ephemeralSlicesSnapshot"?: Record<string, unknown>;
        [key: string]:              unknown;
    };
}

class ErrorReportServiceClass {

    private readonly apiUrl = "/api/errors/report"; // Use proxy configuration from vite.config.ts

    /**
     * Send error report to backend
     */
    async sendErrorReport( error: Error, contexts?: {
        "persistedSlicesSnapshot"?: Record<string, unknown>;
        "ephemeralSlicesSnapshot"?: Record<string, unknown>;
        [key: string]:              unknown;
    } ): Promise<void> {
        try {
            const correlationDiagnostics = buildCorrelationDiagnosticReport();
            const errorReport: ErrorReportData = {
                "message":    error.message,
                "name":       error.name,
                "stack":      error.stack,
                "url":        window.location.href,
                "appVersion": this.getAppVersion(),
                "userAgent":  navigator.userAgent,
                "time":       new Date().toISOString(),
                "contexts":   {
                    ...contexts ?? {},
                    "correlationDiagnostics": correlationDiagnostics,
                },
            };

            await fetch( this.apiUrl, {
                "method":    "POST",
                "headers":   { "Content-Type": "application/json" },
                "body":      JSON.stringify( errorReport ),
                "keepalive": true,
            } );
        } catch( reportError ) {
            console.error( "[ErrorReportService] Failed to send error report:", reportError );

            // Silently fail - don't throw to avoid infinite loops
        }
    }

    /**
     * Get application version from package.json or environment
     */
    private getAppVersion(): string {
        // Try to get version from environment variable first
        const envVersion = import.meta.env.VITE_APP_VERSION;
        if ( envVersion !== undefined && envVersion !== "" ) {
            return envVersion;
        }

        // Fallback to a default version
        return "1.0.0";
    }

    /**
     * Send error report with additional context
     */
    async sendErrorReportWithContext(
        error: Error,
        additionalContext?: Record<string, unknown>,
    ): Promise<void> {
        const contexts = { ...additionalContext };

        await this.sendErrorReport( error, contexts );
    }

}

// Export singleton instance
export const ErrorReportService = new ErrorReportServiceClass();
